// ============================================================================
// phaseKinds.js — SINGLE SOURCE OF TRUTH for phase-kind metadata & behavior
// (2026-08-26, Jaylen: "combo logic for the phase planner").
//
// THE MODEL: a phase's `kind` is either a single BASE segment ('load') or a
// COMBO of segments happening in one day, stored as a composite id joined
// with '+' ('drive+unload+return'). The planner builds combos by merging
// chips (drag onto a chip's center, or the ⊕ between chips) and splits them
// back apart — the picker only ever lists the 8 base kinds.
//
// LEGACY composite ids (load_drive, drive_unload, unload_return, pack_load,
// drive_load_return) remain valid forever — they normalize into segment
// arrays here, so previously saved estimates/jobs keep working untouched.
//
// EVERYTHING downstream derives from segments instead of hardcoded name
// lists: labels, board badge colors, planner chip gradients, truck-chain
// membership, transfer-truck coverage, anchor/date inheritance, hours
// mirroring, portal banners. Before this module those rules lived in ~10
// duplicated maps across the dispatch surfaces and every new combo kind
// meant editing all of them.
// ============================================================================

// Base segments in canonical DISPLAY order (used only for sorting chips in
// the unassigned-pool summary — NOT for ordering segments inside a combo;
// combo order is the user's drive order, e.g. drive→load→return).
const SEG_ORDER = ['pack', 'move', 'load', 'drive', 'unload', 'return', 'hold', 'trash_removal', 'delivery', 'pickup'];

// Per-segment metadata.
//   label / short / abbrev / letter — display names at different densities
//   chip  — planner chip colors (PhaseCalendarStrip)
//   badge — board pill badge colors (Dispatch surfaces)
const SEG = {
  pack: {
    label: 'Pack', short: 'PACK', abbrev: 'PK', letter: 'P',
    description: 'Pack-only day (no loading yet)',
    chip: { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
    badge: { bg: 'rgba(252,211,77,0.85)', fg: '#78350f' },
  },
  move: {
    label: 'Move', short: 'MOVE', abbrev: 'MV', letter: 'M',
    description: 'Standard move day (load + unload same day)',
    chip: { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
    badge: { bg: 'rgba(147,197,253,0.85)', fg: '#1e3a8a' },
  },
  load: {
    label: 'Load', short: 'LOAD', abbrev: 'LD', letter: 'L',
    description: 'Load onto truck, does not unload',
    chip: { bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' },
    badge: { bg: 'rgba(96,165,250,0.95)', fg: '#1e3a8a' },
  },
  drive: {
    label: 'Drive', short: 'DRIVE', abbrev: 'DRV', letter: 'D',
    description: 'Travel / drive day between load and unload',
    chip: { bg: '#fef3c7', color: '#b45309', border: '#fcd34d' },
    badge: { bg: 'rgba(251,191,36,0.95)', fg: '#78350f' },
  },
  unload: {
    label: 'Unload', short: 'UNLOAD', abbrev: 'UNL', letter: 'U',
    description: 'Unload from truck at destination',
    chip: { bg: '#dcfce7', color: '#047857', border: '#86efac' },
    badge: { bg: 'rgba(52,211,153,0.95)', fg: '#064e3b' },
  },
  return: {
    label: 'Return', short: 'RETURN', abbrev: 'RET', letter: 'R',
    description: 'Empty return / deadhead drive back home',
    chip: { bg: '#ede9fe', color: '#6d28d9', border: '#c4b5fd' },
    badge: { bg: 'rgba(167,139,250,0.95)', fg: '#4c1d95' },
  },
  hold: {
    label: 'Hold', short: 'HOLD', abbrev: 'HLD', letter: 'H',
    description: 'Loaded truck parked — commits the truck, no crew (ghost on the board)',
    chip: { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
    badge: { bg: 'rgba(203,213,225,0.95)', fg: '#1e293b' },
  },
  trash_removal: {
    label: 'Trash removal', short: 'TRASH', abbrev: 'TR', letter: 'T',
    description: 'Dump run / trash removal day',
    chip: { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
    badge: { bg: 'rgba(252,165,165,0.95)', fg: '#7f1d1d' },
  },
  // Small-job legs (moves-architecture.md rule 7, 2026-09-18): a delivery or
  // pickup can be its own one-leg move (Quick Dispatch) OR a leg on a bigger
  // move — the bedroom set picked up between LOAD and UNLOAD. Legs carry
  // their own stop via move_jobs.from/to_address_override.
  delivery: {
    label: 'Delivery', short: 'DELIVERY', abbrev: 'DLV', letter: 'V',
    description: 'Deliver items (own stop address)',
    chip: { bg: '#ccfbf1', color: '#0f766e', border: '#5eead4' },
    badge: { bg: 'rgba(94,234,212,0.95)', fg: '#134e4a' },
  },
  pickup: {
    label: 'Pickup', short: 'PICKUP', abbrev: 'PKU', letter: 'K',
    description: 'Pick items up (own stop address)',
    chip: { bg: '#ccfbf1', color: '#0f766e', border: '#5eead4' },
    badge: { bg: 'rgba(45,212,191,0.95)', fg: '#134e4a' },
  },
};

// Legacy composite ids → segment arrays (drive order). These stay valid
// as stored kinds forever; new combos serialize as 'a+b+c'.
const LEGACY_COMBOS = {
  load_drive: ['load', 'drive'],
  drive_unload: ['drive', 'unload'],
  unload_return: ['unload', 'return'],
  pack_load: ['pack', 'load'],
  drive_load_return: ['drive', 'load', 'return'],
};

// ── Core resolvers ─────────────────────────────────────────────────────────

// Any kind string → ordered segment array. Unknown strings come back as a
// single-element array so old/foreign data never explodes a render.
export function kindSegments(kind) {
  const k = kind || 'move';
  if (SEG[k]) return [k];
  if (LEGACY_COMBOS[k]) return LEGACY_COMBOS[k];
  if (k.includes('+')) {
    const segs = k.split('+').filter(s => SEG[s]);
    if (segs.length > 0) return segs;
  }
  return [k];
}

// Segment array → canonical stored id. Prefers the legacy name when the
// segments match one exactly (keeps old data & new data converging on the
// same ids); otherwise joins with '+'.
export function kindId(segments) {
  const segs = (segments || []).filter(Boolean);
  if (segs.length === 0) return 'move';
  if (segs.length === 1) return segs[0];
  const legacy = Object.entries(LEGACY_COMBOS).find(([, v]) =>
    v.length === segs.length && v.every((s, i) => s === segs[i]));
  return legacy ? legacy[0] : segs.join('+');
}

export function isValidKind(kind) {
  if (!kind) return false;
  const segs = kindSegments(kind);
  return segs.length > 0 && segs.every(s => !!SEG[s]);
}

export function isComboKind(kind) {
  return kindSegments(kind).length > 1;
}

// ── Display ────────────────────────────────────────────────────────────────

// "Drive/Unload/Return"
export function kindLabel(kind) {
  const segs = kindSegments(kind);
  return segs.map(s => SEG[s]?.label || s).join('/');
}

// Singles: "LOAD" · combos: "DRV/UNL/RET"
export function kindShortLabel(kind) {
  const segs = kindSegments(kind);
  if (segs.length === 1) return SEG[segs[0]]?.short || String(kind).toUpperCase();
  return segs.map(s => SEG[s]?.abbrev || s).join('/');
}

// High-zoom single-letter form: "L" · "D/U/R"
export function kindLetter(kind) {
  const segs = kindSegments(kind);
  return segs.map(s => SEG[s]?.letter || (s[0] || '?').toUpperCase()).join('/');
}

// Board badge — combos take the LAST segment's colors (the day's outcome:
// load_drive reads amber like DRIVE, drive_unload reads emerald like
// UNLOAD — matches the palette the board has always used) + generated label.
export function kindBadge(kind) {
  const segs = kindSegments(kind);
  const last = SEG[segs[segs.length - 1]] || SEG.move;
  return { bg: last.badge.bg, fg: last.badge.fg, label: kindShortLabel(kind) };
}

// Planner chip style — combos render a hard-stop gradient across their
// segments' colors (the same split-diagonal look the hand-made combo
// styles used), text/border from the last segment.
export function kindChipStyle(kind) {
  const segs = kindSegments(kind);
  if (segs.length === 1) {
    const s = SEG[segs[0]] || SEG.move;
    return { bg: s.chip.bg, color: s.chip.color, border: s.chip.border };
  }
  const n = segs.length;
  const stops = segs.map((s, i) => {
    const c = (SEG[s] || SEG.move).chip.bg;
    return `${c} ${Math.round((i * 100) / n)}% ${Math.round(((i + 1) * 100) / n)}%`;
  }).join(', ');
  const last = SEG[segs[n - 1]] || SEG.move;
  return { bg: `linear-gradient(135deg, ${stops})`, color: last.chip.color, border: last.chip.border };
}

// Sort key for listing mixed kinds (unassigned-pool chips): first segment's
// canonical position, singles before combos at the same position.
export function kindSortKey(kind) {
  const segs = kindSegments(kind);
  const idx = SEG_ORDER.indexOf(segs[0]);
  return (idx === -1 ? 99 : idx) * 10 + Math.min(9, segs.length - 1);
}

// ── Behavior predicates (replace the hardcoded name-lists) ────────────────

// Does this day's work involve the truck at all? (pack-only days don't.)
const TRUCK_SEGS = new Set(['load', 'move', 'hold', 'drive', 'unload', 'return', 'trash_removal', 'delivery', 'pickup']);
export function kindUsesTruck(kind) {
  return kindSegments(kind).some(s => TRUCK_SEGS.has(s));
}

// Trip truck-CHAIN membership: the truck physically committed from load
// through return. Dump runs use their own trucks → excluded; pack-only
// days excluded. (Replaces TRUCK_CHAIN_KINDS.)
const CHAIN_SEGS = new Set(['load', 'move', 'hold', 'drive', 'unload', 'return']);
export function kindInTruckChain(kind) {
  return kindSegments(kind).some(s => CHAIN_SEGS.has(s));
}

// Transfer-truck coverage sides (replaces TRANSFER_LOAD_KINDS /
// TRANSFER_UNLOAD_KINDS): a leg is load-side if any segment loads the
// truck, unload-side if any segment unloads it.
export function kindLoadsTruck(kind) {
  return kindSegments(kind).some(s => s === 'load' || s === 'move');
}
export function kindUnloadsTruck(kind) {
  return kindSegments(kind).some(s => s === 'unload' || s === 'move');
}

// Anchor phases inherit the estimate's move_date when dateless (replaces
// the move/load/drive_load_return list — anything containing a load or a
// move IS the trip's main day).
export function kindIsAnchor(kind) {
  return kindLoadsTruck(kind);
}

// Packing-related day: inherits est.pack_date when dateless, surfaces
// packing materials in the crew portal. (pack + pack_load previously.)
export function kindIsPack(kind) {
  return kindSegments(kind).includes('pack');
}

// PACK-ONLY day: packing requirements (packing crew + packing vehicle)
// replace the whole-move allocation; no truck reset photo.
export function kindIsPackOnly(kind) {
  const segs = kindSegments(kind);
  return segs.length === 1 && segs[0] === 'pack';
}

// Labor segments — the ones billed at the estimate's hourly_rate.
const LABOR_SEGS = new Set(['move', 'load', 'unload', 'trash_removal', 'delivery', 'pickup']);
export function kindHasLabor(kind) {
  return kindSegments(kind).some(s => LABOR_SEGS.has(s));
}

// ── Ghost holds (Jensen 2026-09-17, Hold Days Showroom pick C) ────────────
// A HOLD-only day is a loaded truck sitting parked between LOAD and DRIVE.
// It commits the TRUCK, never the crew: the row carries no crew_ids / no
// leader, so it stays off the crew rows, out of the Unassigned pool, off
// the crew portal and out of crew capacity — the board draws it as a grey
// "HOLD · client" chip at the bottom of the loader's cell (and on the
// Trucks view). Every save path funnels through ghostHoldFields() so a
// drop on a crew row, a cascade, a trip-card drop or an AssignModal save
// can't accidentally put a person on a hold day.
export function kindIsHoldOnly(kind) {
  const segs = kindSegments(kind);
  return segs.length === 1 && segs[0] === 'hold';
}

const TERMINAL = new Set(['completed', 'cancelled']);
export function ghostHoldFields(job) {
  if (!job || !kindIsHoldOnly(job.phase_kind)) return null;
  const status = TERMINAL.has(job.status) ? job.status : (job.move_date ? 'assigned' : 'unassigned');
  return { crew_ids: [], leader_id: null, status };
}

// Which estimate-level hours field mirrors into move_jobs.duration when
// the phase carries no per-phase hours: any labor segment → estimated_hours;
// else a pack segment → packing_hours; else null (drive/hold/return).
// (2026-09-10: labor now wins over pack for pack+move combos — the day is
// mostly a move day; pushPhasesToDispatch adds the packing hours on top.)
export function kindHoursSource(kind) {
  if (kindHasLabor(kind)) return 'labor';
  if (kindIsPack(kind)) return 'packing';
  return null;
}

// ── Per-phase pricing slots (2026-09-10) ───────────────────────────────────
// A phase's est_min_hours / est_max_hours / hourly_rate are that day's
// LABOR pricing (multi-day pricing, 2026-06-09) — EXCEPT on a pack-only day,
// where the same three fields hold the PACKING pricing (multi-day packing,
// 2026-08-25). When a pack chip is merged into a labor day the packing side
// moves to pack_min_hours / pack_max_hours / pack_rate so the two never
// blend. Before this, merging pack (3h × $125) onto a move day summed the
// hours and kept the pack rate, and every pricing surface then read the
// combo as "Day 1 labor: 3 hrs × $125" (Alwin, BBM-2609-018). Always read
// pricing through these two accessors — never the raw fields.
const num = (v) => Number(v) || 0;
export function phaseLaborPricing(p) {
  if (!p || kindIsPackOnly(p.kind)) return { min: 0, max: 0, rate: 0 };
  const min = num(p.est_min_hours);
  return { min, max: num(p.est_max_hours) || min, rate: num(p.hourly_rate) };
}
export function phasePackPricing(p) {
  if (!p || !kindIsPack(p.kind)) return { min: 0, max: 0, rate: 0 };
  if (kindIsPackOnly(p.kind)) {
    const min = num(p.est_min_hours);
    return { min, max: num(p.est_max_hours) || min, rate: num(p.hourly_rate) };
  }
  const min = num(p.pack_min_hours);
  return { min, max: num(p.pack_max_hours) || min, rate: num(p.pack_rate) };
}
export const phaseHasLaborPricing = (p) => { const l = phaseLaborPricing(p); return l.min > 0 || l.max > 0; };
export const phaseHasPackPricing  = (p) => { const k = phasePackPricing(p); return k.rate > 0 && (k.min > 0 || k.max > 0); };

// Inverse: put a labor {min,max,rate} and a pack {min,max,rate} into the
// right slots for `kind` (merge/split in PhaseCalendarStrip). Pack-only
// kinds carry their packing pricing in est_*; everything else keeps labor
// in est_* and packing (if the kind packs at all) in pack_*.
export function placePhasePricing(kind, labor, pack) {
  const l = labor || { min: 0, max: 0, rate: 0 };
  const k = pack || { min: 0, max: 0, rate: 0 };
  if (kindIsPackOnly(kind)) {
    return { est_min_hours: k.min, est_max_hours: k.max, hourly_rate: k.rate, pack_min_hours: 0, pack_max_hours: 0, pack_rate: 0 };
  }
  const packs = kindIsPack(kind);
  return {
    est_min_hours: l.min, est_max_hours: l.max, hourly_rate: l.rate,
    pack_min_hours: packs ? k.min : 0, pack_max_hours: packs ? k.max : 0, pack_rate: packs ? k.rate : 0,
  };
}

// ── The base-kind list (what pickers render) ──────────────────────────────
// Same shape as the old PHASE_KINDS export ({key,label,shortLabel,
// description}) so existing consumers keep working — just 8 base entries
// now instead of 13 (combos are built by merging, not picked).
export const PHASE_KINDS = SEG_ORDER.map(key => ({
  key,
  label: SEG[key].label,
  shortLabel: SEG[key].short,
  description: SEG[key].description,
}));

export default {
  PHASE_KINDS, kindSegments, kindId, isValidKind, isComboKind,
  kindLabel, kindShortLabel, kindLetter, kindBadge, kindChipStyle, kindSortKey,
  kindUsesTruck, kindInTruckChain, kindLoadsTruck, kindUnloadsTruck,
  kindIsAnchor, kindIsPack, kindIsPackOnly, kindHasLabor, kindHoursSource,
  phaseLaborPricing, phasePackPricing, phaseHasLaborPricing, phaseHasPackPricing, placePhasePricing,
};
